import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import sample from 'lodash/sample';
import debounce from 'lodash/debounce';
import isString from 'lodash/isString';
import classNames from 'classnames';
import equal from 'fast-deep-equal';

import styles from 'src/styles/common/templateGallery.module.scss';
import { SearchIcon } from 'src/components/Icons/Search';
import { UiCleanInput } from 'src/components/UI/inputs/CleanInput';
import { StateFormReturnType } from 'src/utils/stateForm';
import { FormValues } from 'src/components/Modals/TemplateGallery/screens/All/index';
import { useNsTHelper } from 'src/utils/i18n';

type Props = {
  formProps: StateFormReturnType<FormValues>;
  templatesNames: string[];
  getTemplates: () => void;
};

export const TemplateGallerySearchInput: FC<Props> = ({ formProps, templatesNames, getTemplates }) => {
  const tHelper = useNsTHelper('modals.templateGallery', 'all');

  const [showPlaceholder, setShowPlaceholder] = useState(!formProps.getValue('search'));

  const memoizedGetTemplates = useMemo(() => debounce(getTemplates, 300), [getTemplates]);

  const handleChange = useCallback(
    (value: string | null | undefined) => {
      setShowPlaceholder(!value);

      if (isString(value) || !value) {
        memoizedGetTemplates();
      }
    },
    [memoizedGetTemplates]
  );

  const ROTATE_TIME = 3000;

  const unusedNamesRef = useRef<string[]>([]);
  const prevTemplatesNamesRef = useRef<string[]>([]);

  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (templatesNames.length === 1) {
      setTemplateName(templatesNames[0]);
    }

    if (showPlaceholder && templatesNames.length > 1) {
      const recalculateTemplatesNames = () => {
        const unusedNames = unusedNamesRef.current;
        let name: string | undefined;

        if (unusedNames.length > 1) {
          name = sample(unusedNames);

          unusedNamesRef.current = unusedNamesRef.current.filter((item) => item !== name);
        } else {
          [name] = unusedNames;

          unusedNamesRef.current = templatesNames.filter((item) => item !== name);
        }

        if (name) {
          setTemplateName(name);
        }
      };

      if (!equal(prevTemplatesNamesRef.current, templatesNames)) {
        prevTemplatesNamesRef.current = [...templatesNames];

        const initialTemplateName = sample(templatesNames) || '';
        unusedNamesRef.current = templatesNames.filter((item) => item !== initialTemplateName);

        recalculateTemplatesNames();
      }

      interval = setInterval(() => recalculateTemplatesNames(), ROTATE_TIME);
    }

    return () => {
      clearInterval(interval);
    };
  }, [showPlaceholder, templatesNames]);

  return (
    <UiCleanInput
      material
      formProps={formProps}
      name="search"
      placeholder={
        showPlaceholder ? (
          <div className={styles.placeholder}>
            <span>{tHelper('search')}&nbsp;</span>
            <div className={styles.templateNameWrapper}>
              {templatesNames.map((item) => (
                <div key={item} className={classNames(styles.text, templateName === item && styles.fadeIn)}>
                  {item}
                </div>
              ))}
            </div>
          </div>
        ) : undefined
      }
      wrapperClassName={styles.inputWrapper}
      className={styles.input}
      prefix={<SearchIcon className={styles.icon} />}
      onChange={handleChange}
    />
  );
};
